import type { PatientNotePage } from "@allergy123/contracts/clinical";
import { Button, Card, Field, Status, Table, Td, Th } from "@allergy123/ui";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { useClinical, useLoad } from "../api";
import { errorText, isNotFound } from "../lib/errors";
import { SEX_LABELS, ageOn, formatDateTime, fullName, kindLabel, statusLabel } from "../lib/format";
import { BenefitsCard, DemographicsCard, InsuranceCard, PhotoCard } from "../patient/RecordCards";
import { useSession } from "../session";

/** The clinic chooses the visit type (D-014): Test, Retest after a negative or bad first result, Continuation year over year. */
const KINDS = ["test", "retest", "continuation", "office_visit", "adjustment"] as const;

/** One patient: demographics, encounters, notes, and the walk-in start buttons (Q-69). */
export function PatientPage() {
  const { id = "" } = useParams();
  const s = useSession();
  const api = useClinical();
  const navigate = useNavigate();
  const data = useLoad((a) => a.patients.get(id), [id]);
  const notes = useLoad((a) => a.patients.listNotes(id, { limit: 20 }), [id]);
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const timezone = s.location?.timezone ?? "America/Denver";

  async function start(kind: (typeof KINDS)[number]) {
    if (!data.data) return;
    setBusy(kind);
    setNote(null);
    try {
      const detail = await api.encounters.create({ patientId: id, locationId: s.location?.locationId ?? data.data.primaryLocationId, kind });
      navigate(`/encounters/${detail.encounter.encounterId}`);
    } catch (err) {
      setNote(`Couldn't start the ${kindLabel(kind).toLowerCase()}: ${errorText(err)}`);
      setBusy(null);
    }
  }

  if (data.state === "error") {
    return (
      <Card tone="tinted">
        <h1 className="mb-2 text-h2 font-semibold text-anchor">{isNotFound(data.error) ? "Patient not found" : "Couldn't load the patient"}</h1>
        <p className="mb-4 text-ink-secondary">{errorText(data.error)}</p>
        <Button variant="secondary" surface="card" href="/patients">
          Back to patients
        </Button>
      </Card>
    );
  }
  const p = data.data;
  if (!p) return <p className="text-ink-secondary">Loading…</p>;
  const openDraft = p.recentEncounters.find((e) => e.status === "draft");

  return (
    <>
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="mb-1 text-small text-ink-secondary">
            <Link to="/patients">Patients</Link>
          </p>
          <h1 className="mb-2 text-h1 font-semibold text-anchor">{fullName(p)}</h1>
          <p className="text-ink-secondary">
            Born {p.dateOfBirth} ({ageOn(p.dateOfBirth)}) · {SEX_LABELS[p.sex] ?? p.sex} · {p.status === "active" ? "Active" : statusLabel(p.status)}
          </p>
        </div>
        {s.can("clinical:write") && (
          <div className="flex flex-wrap gap-2">
            {openDraft ? (
              <Button href={`/encounters/${openDraft.encounterId}`}>Resume {kindLabel(openDraft.kind).toLowerCase()}</Button>
            ) : (
              KINDS.map((kind) => (
                <Button key={kind} variant={kind === "test" ? "primary" : "secondary"} disabled={busy !== null} onClick={() => void start(kind)}>
                  Start {kindLabel(kind).toLowerCase()}
                </Button>
              ))
            )}
          </div>
        )}
      </div>
      {note && (
        <div className="mb-6">
          <Status kind="error" label={note} />
        </div>
      )}
      <div className="grid gap-6 lg:grid-cols-[2fr_1fr]">
        <div className="flex flex-col gap-6">
          <Card as="section" aria-labelledby="encounters">
            <h2 id="encounters" className="mb-4 text-h3 font-semibold text-anchor">
              Encounters
            </h2>
            <Table caption={`${p.recentEncounters.length} recent`}>
              <thead>
                <tr>
                  <Th>Started</Th>
                  <Th>Kind</Th>
                  <Th>Status</Th>
                  <Th>Result</Th>
                  <Th>Report</Th>
                </tr>
              </thead>
              <tbody>
                {p.recentEncounters.length === 0 && (
                  <tr>
                    <Td colSpan={5} className="text-ink-secondary">
                      No encounters yet.
                    </Td>
                  </tr>
                )}
                {p.recentEncounters.map((e) => (
                  <tr key={e.encounterId}>
                    <Td>
                      <Link to={`/encounters/${e.encounterId}`}>{formatDateTime(e.startedAt, timezone)}</Link>
                    </Td>
                    <Td>{kindLabel(e.kind)}</Td>
                    <Td>{statusLabel(e.status)}</Td>
                    <Td>{e.hasResult ? "Yes" : "—"}</Td>
                    <Td>{e.documentStates[0] ? statusLabel(e.documentStates[0].status) : "—"}</Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>
          <Notes patientId={id} notes={notes} timezone={timezone} />
        </div>
        <div className="flex flex-col gap-6">
          <DemographicsCard key={`d-${p.version}`} p={p} onSaved={data.reload} />
          <InsuranceCard key={`i-${p.version}`} p={p} onSaved={data.reload} />
          <BenefitsCard key={`b-${p.version}`} p={p} onSaved={data.reload} />
          <PhotoCard key={`p-${p.version}-${p.photoDocumentId ?? ""}`} p={p} onSaved={data.reload} />
        </div>
      </div>
    </>
  );
}

function Notes({ patientId, notes, timezone }: { patientId: string; notes: { state: string; data?: PatientNotePage; error?: Error; reload: () => void }; timezone: string }) {
  const s = useSession();
  const api = useClinical();
  const [body, setBody] = useState("");
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function add(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.patients.addNote(patientId, { title: title.trim() || null, body: body.trim() });
      setBody("");
      setTitle("");
      notes.reload();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card as="section" aria-labelledby="notes">
      <h2 id="notes" className="mb-4 text-h3 font-semibold text-anchor">
        Notes
      </h2>
      {notes.state === "error" && <Status kind="error" label={`Couldn't load notes: ${errorText(notes.error)}`} className="mb-4 block" />}
      <ul className="mb-6 flex flex-col gap-4">
        {notes.data?.items.length === 0 && <li className="text-ink-secondary">No notes yet.</li>}
        {notes.data?.items.map((n) => (
          <li key={n.noteId} className="border-b border-rule-subtle pb-3">
            <p className="text-small text-ink-secondary">
              {n.authorName} · {formatDateTime(n.createdAt, timezone)}
              {n.amendsNoteId && " · amends an earlier note"}
            </p>
            {n.title && <p className="font-medium">{n.title}</p>}
            <p className="whitespace-pre-wrap">{n.body}</p>
          </li>
        ))}
      </ul>
      {s.can("clinical:write") && (
        <form className="flex flex-col gap-3" onSubmit={(e) => void add(e)}>
          <Field label="Title (optional)" wide value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} />
          <label className="flex flex-col gap-2 text-small font-medium text-ink">
            Note
            <textarea className="min-h-24 rounded-sm border border-rule-strong bg-card px-3 py-2 text-body font-normal text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus" value={body} onChange={(e) => setBody(e.target.value)} maxLength={8000} required />
          </label>
          {error && <Status kind="error" label={error} />}
          <div>
            <Button type="submit" variant="secondary" surface="card" disabled={busy || !body.trim()}>
              Add note
            </Button>
          </div>
        </form>
      )}
    </Card>
  );
}
